// Single source for Cloudinary upload + display-time URL helpers.

import { fetchAPI } from '../ducks/api';

/**
 * Upload a file to Cloudinary where the server says it belongs.
 *
 * `kind` says what it is — progress_photo, measurement_photo, broadcast, logo,
 * cover, avatar, exercise_video (the API's helpers/media.js) — and `clientId`
 * whose it is when a coach uploads for a client. The API checks the caller may
 * upload that for that person and signs the folder + tags (filed under the
 * coach and client, tagged so a client's media can be found or deleted later);
 * the file then goes straight from the browser to Cloudinary, which rejects
 * any other folder or tags. `file` is a File/Blob or a data URI. Resolves to
 * the secure URL; throws on failure — callers surface the error.
 *
 * `conversationId` files chat media under that conversation's client, and
 * `resource` ('image' | 'video' | 'audio') says what a chat file is (photos
 * are kept to 1600px).
 * `invite` ({ token, email }) signs with a trainer invite instead of a session
 * — the logo on the trainer-setup page, before the account exists.
 */
export async function uploadMedia(
  file,
  kind,
  { clientId, conversationId, resource, invite, timeoutMs = 120000 } = {}
) {
  const sig = invite
    ? await fetchAPI('/media/sign-invite', { method: 'POST' }, invite)
    : await fetchAPI('/media/sign', { method: 'POST' }, { kind, clientId, conversationId, resource });
  if (!sig?.uploadUrl || !sig?.params?.signature) throw new Error('Could not sign the upload');
  const formData = new FormData();
  formData.append('file', file);
  Object.entries(sig.params).forEach(([key, value]) => formData.append(key, value));
  // Bound the upload so a stalled connection can't spin the UI forever.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  let res;
  try {
    res = await fetch(sig.uploadUrl, { method: 'POST', body: formData, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
  if (!res.ok) throw new Error('Cloudinary upload failed');
  const data = await res.json();
  if (!data?.secure_url) throw new Error('Cloudinary returned no URL');
  return data.secure_url;
}

/**
 * A coach's exercise video FILE (`<input type="file" accept="video/*">`).
 * Size is pre-checked client-side; videos get a longer timeout.
 */
export function uploadVideoToCloudinary(file) {
  return uploadMedia(file, 'exercise_video', { timeoutMs: 300000 }); // 5 min
}

/**
 * Rewrite a Cloudinary URL to pad the image to a uniform aspect
 * ratio with transparent background. Returns the input unchanged
 * for non-Cloudinary URLs (defensive — branding logos could come
 * from S3, a CDN, or another provider in the future).
 *
 * Why pad at display time and not at upload time: trainers upload
 * logos at wildly varying native aspect ratios (square, wordmark,
 * stacked). Padding via URL transformation gives every logo the
 * same visual footprint in the sidebar without modifying the
 * stored asset — the Settings preview still shows the trainer's
 * original upload. Cloudinary caches the transformed result on
 * first hit, so subsequent renders are CDN-fast.
 *
 * Default aspect `5:1` matches the sidebar's 140×28 frame, which is
 * itself sized to mirror where the Nubod wordmark (intrinsic 4.84:1)
 * sits when no trainer branding is set. Pass a different aspect for
 * other surfaces (email banner, share cards) — `'3:1'` is a more
 * forgiving option if a surface needs to accommodate both wordmarks
 * and stacked marks at comparable visual weight.
 */
export function padLogoUrl(url, aspect = '5:1') {
  if (!url || typeof url !== 'string') return url;
  if (!url.includes('/image/upload/')) return url;
  // Idempotent: if the URL already carries our padding transform,
  // don't double up. Guards against re-renders that re-derive the
  // URL from already-transformed input.
  if (url.includes('/image/upload/c_pad,')) return url;
  return url.replace(
    '/image/upload/',
    `/image/upload/c_pad,ar_${aspect},b_transparent,f_auto,q_auto/`
  );
}

/**
 * Rewrite a Cloudinary URL to a FILL-cropped, right-sized, optimized
 * derivative for cover images. `c_fill` + `g_auto` (content-aware gravity)
 * crop any source — portrait, landscape, huge, tiny — to the requested
 * `width`×`height` while keeping the salient subject in frame, so a trainer
 * can upload whatever they have and it always frames sensibly. `q_auto,f_auto`
 * cut bytes / pick a modern format. Returns the input unchanged for
 * non-Cloudinary URLs; idempotent against our own transform.
 *
 * We normalize at DISPLAY time (not upload) so the stored asset stays the
 * trainer's original — the builder can re-derive at any size, and each surface
 * (mobile card, detail hero) requests its own dimensions.
 */
export function cloudinaryFill(url, width, height) {
  if (!url || typeof url !== 'string') return url;
  if (!url.includes('/image/upload/')) return url;
  if (url.includes('/image/upload/c_fill,')) return url;
  return url.replace(
    '/image/upload/',
    `/image/upload/c_fill,g_auto,w_${width},h_${height},q_auto,f_auto/`
  );
}
